import type { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

// Components
import HelpCollection from '@/components/pages/HelpCenter/components/Collection'
import HelpHead from '@/components/pages/HelpCenter/components/HelpHead'

// Libs
import { fetchHelpCollection, FetchHelpCollectionResponse } from '@/api/fetchHelpCollection'
import { fetchHelpCollections } from '@/api/fetchHelpCollections'
import {
  countryQueryOf,
  getHelpCountry,
  helpCollectionPath,
  isHelpNotFoundError,
  parseHelpId,
  requireHelpData,
} from '@/components/pages/HelpCenter/methods'

// Types
import type { HelpCollectionItem } from '@/api/fetchHelpArticle'

type Props = {
  data: FetchHelpCollectionResponse
  collections: HelpCollectionItem[]
  countryCode: string
  countryQuery: string | null
}

// _app 以 <Component {...appProps} /> 渲染，getServerSideProps 的資料在 props.pageProps
const HelpCollectionPage = ({ pageProps }: { pageProps: Props }) => {
  const { data, collections, countryCode, countryQuery } = pageProps

  return (
    <>
      <HelpHead title={data.collection.name} description={data.collection.description} canonicalPath={helpCollectionPath(data.collection.id)} />
      <HelpCollection data={data} collections={collections} countryCode={countryCode} countryQuery={countryQuery ?? undefined} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, params, query }) => {
  const id = parseHelpId(params?.id)
  if (!id) return { notFound: true }

  const countryCode = getHelpCountry(locale, query.country_code)
  try {
    // 側欄的分類清單跟分類頁一起抓（後端都從同一份站點快取算）
    const [data, collectionsRes] = await Promise.all([fetchHelpCollection({ id, countryCode }), fetchHelpCollections({ countryCode })])
    return {
      props: {
        data: requireHelpData(data),
        collections: collectionsRes?.list ?? [],
        countryCode,
        countryQuery: countryQueryOf(query.country_code) ?? null,
        ...(await serverSideTranslations(locale as string, ['common'])),
      },
    }
  } catch (error) {
    if (isHelpNotFoundError(error)) return { notFound: true }
    throw error
  }
}

export default HelpCollectionPage
